import { useEffect } from 'react'

export const useScrollToFirstError = (errors, submitCount) => {
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!errors || Object.keys(errors).length === 0) return
    if (!submitCount || submitCount <= 0) return

    const firstErrorKey = Object.keys(errors)[0]
    if (!firstErrorKey) return

    const target = document.querySelector(`[name="${firstErrorKey}"]`)
    if (!target) return

    const isElementVisible = (el) => {
      if (!el) return false
      if (el.type === 'hidden') return false
      const style = window.getComputedStyle(el)
      if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
        // note: opacity 0 might still occupy space; treat as hidden for UX
        return false
      }
      // offsetParent is null for display:none or detached; client rects check covers zero-size
      return el.offsetParent !== null || el.getClientRects().length > 0
    }

    const findScrollableAnchor = (el) => {
      if (!el) return null
      if (isElementVisible(el)) return el
      // Try common visible wrappers or labels
      const wrapper = el.closest(
        '.file-form-element, .form-element, .form-group, .field, .MuiFormControl-root'
      )
      if (wrapper && isElementVisible(wrapper)) return wrapper
      // Try label associated via for="id"
      if (el.id) {
        const label = document.querySelector(`label[for="${el.id}"]`)
        if (label && isElementVisible(label)) return label
      }
      // Fallback to nearest visible parent
      let parent = el.parentElement
      while (parent) {
        if (isElementVisible(parent)) return parent
        parent = parent.parentElement
      }
      return null
    }

    const findFocusable = (root) => {
      if (!root) return null
      const focusableSelectors = [
        'button',
        '[role="button"]',
        'input:not([type="hidden"]):not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ]
      // Prefer elements within the same wrapper
      const withinRoot = root.querySelector(focusableSelectors.join(', '))
      if (withinRoot && isElementVisible(withinRoot)) return withinRoot
      // Otherwise, prefer the target itself if visible
      if (isElementVisible(target)) return target
      return null
    }

    const scrollAnchor = findScrollableAnchor(target)
    if (scrollAnchor && typeof scrollAnchor.scrollIntoView === 'function') {
      scrollAnchor.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    const focusTarget = findFocusable(scrollAnchor || target)
    if (focusTarget && typeof focusTarget.focus === 'function') {
      try {
        focusTarget.focus({ preventScroll: true })
      } catch (e) {
        focusTarget.focus()
      }
    }
  }, [errors, submitCount])
}
